import { autoUpdate, inline, offset, useFloating } from '@floating-ui/react';
import { isAgentThread, useStores } from '@littlebird/sdk';
import { useParams } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';

import { AppLogo } from '@/assets/icons/AppLogo';
import { Icons } from '@/assets/icons/icons';
import { MainLayoutPanelTunnel } from '@/components/blocks/MainLayout/main-layout-tunnel';
import { mainLayoutStore } from '@/components/blocks/MainLayout/main-layout.store';
import { Button, IconButton, Tap, XStack } from '@/components/ui';
import { stickyHideWithScroll } from '@/components/ui/editor/utils/floating-ui';
import { AgentThreadHost } from '@/features/agents/agents-thread-host';
import { isAgentsUxEnabled } from '@/features/agents/agents.utils';
import { chatRowLayoutStore } from '@/features/chat/chat-row-layout.store';
import { FilePreviewHostContext } from '@/features/chat/file-preview-host';
import { FilePreviewSidebar } from '@/features/chat/FilePreviewSidebar';
import { chatActions } from '@/features/cmd-bar/actions/chat';
import useCommandBarActions from '@/features/cmd-bar/use-command-bar-actions';
import { HivemindBanner } from '@/features/hivemind';
import { SandboxTerminalPanel } from '@/features/sprites/SandboxTerminalPanel';
import { useChromelessSurface } from '@/hooks/useChromelessSurface';
import { useTextSelection } from '@/hooks/useTextSelection';
import { logError } from '@/lib/errorLogging';
import { uiStore } from '@/stores/ui.store';
import { logChatRenderDebug } from '@/utils/chat-render-debug';
import { classList } from '@/utils/cn';
import { NEW_CHAT_ID } from '@/utils/constants';
import { handleCopyText } from '@/utils/platform';

import { CHAT_THREAD_ID } from '../Message/MessageList/useChatScroll';

import { Thread } from './Thread';

const Chat = observer(() => {
  const { chatId } = useParams({ strict: false });
  const threadId = chatId ?? NEW_CHAT_ID;
  const { threads } = useStores();
  const thread = threads.getById(threadId);
  const isAgentChat =
    isAgentsUxEnabled() && thread !== undefined && isAgentThread(thread);
  const [resolvedChatId, setResolvedChatId] = useState<string>();

  useEffect(() => {
    if (!chatId) return;
    let cancelled = false;
    const markResolved = () => {
      if (!cancelled) setResolvedChatId(chatId);
    };
    void threads.ensureLoaded(chatId).then(markResolved, markResolved);
    return () => {
      cancelled = true;
    };
  }, [chatId, threads]);

  const isThreadPending =
    chatId !== undefined && thread === undefined && resolvedChatId !== chatId;
  useChromelessSurface(isAgentChat);
  logChatRenderDebug('chat-render', { isAgentChat, isThreadPending }, threadId);

  useCommandBarActions(chatActions, [], { threadId });

  const isDraggingFile = uiStore.isDraggingFile;

  const contentWidth = mainLayoutStore.contentWidth;
  useEffect(() => {
    chatRowLayoutStore.setRowWidth(contentWidth);
  }, [contentWidth]);

  return (
    <div className={classList('h-main-outlet', 'relative')}>
      <XStack className="h-main-outlet min-h-main-outlet max-h-main-outlet select-text">
        <FilePreviewHostContext.Provider value={threadId}>
          {isAgentChat ? (
            <AgentThreadHost threadId={threadId} />
          ) : isThreadPending ? (
            <div className="flex-1" />
          ) : (
            <Thread
              threadId={threadId}
              contentHeader={<HivemindBanner threadId={threadId} />}
            />
          )}
        </FilePreviewHostContext.Provider>
      </XStack>
      <MainLayoutPanelTunnel.In>
        <FilePreviewSidebar chatId={threadId} insetTop />
        <SandboxTerminalPanel chatId={threadId} overlay />
      </MainLayoutPanelTunnel.In>
      <IncludeSelectionButton visible={!isDraggingFile} threadId={threadId} />
    </div>
  );
});

export default Chat;

type IncludeSelectionButtonProps = {
  visible: boolean;
  threadId: string;
  container?: HTMLElement | null;
};

export const IncludeSelectionButton = ({
  visible,
  threadId,
  container: providedContainer,
}: IncludeSelectionButtonProps) => {
  const [fallbackContainer, setFallbackContainer] =
    useState<HTMLElement | null>(null);

  useEffect(() => {
    if (providedContainer) return;
    const elem = document.getElementById(`${CHAT_THREAD_ID}-${threadId}`);
    setFallbackContainer(elem ?? document.body);
  }, [providedContainer, threadId]);

  const container = providedContainer ?? fallbackContainer;
  if (!container) return null;

  return (
    <IncludeSelectionView
      visible={visible}
      container={container}
      threadId={threadId}
    />
  );
};

/**
 * Renders a floating button for text selection with sticky behavior at scroll edges.
 *
 * Uses Victor's substitution technique (dual-render) to achieve jank-free sticky positioning:
 * 1. Container version: Always rendered in scroll container with position: absolute.
 *    Scrolls naturally with the container (no scroll tracking jank) - no JavaScript needed during scroll!
 *    Hidden (opacity: 0) when sticky, but continues tracking to detect when to exit sticky mode.
 * 2. Body clone: Only rendered when sticky, portaled to document.body with position: fixed.
 *    Uses frozen viewport coordinates from middleware, stays put without recalculation (no jank).
 *
 * Why this works: The container version scrolls naturally (position: absolute in scroll container),
 * so Floating UI can detect when the selection moves away from the edge to exit sticky mode.
 * The body clone uses position: fixed, so it doesn't need any JavaScript updates while scrolling.
 *
 * Key behaviors:
 * - Normal scrolling: Container version scrolls naturally, no JS recalculation
 * - At edge: Enters sticky mode, shows fixed clone, hides container version
 * - Scrolling away: Container version detects selection moved, exits sticky automatically
 * - No initial animation on clone to prevent flash when entering sticky mode
 */
const IncludeSelectionView = ({
  visible,
  container,
  threadId,
}: IncludeSelectionButtonProps & { container: HTMLElement }) => {
  const { drafts: draftsStore } = useStores();

  const buttonRef = useRef<HTMLButtonElement>(null);

  const { selection, clearSelection, isSelecting } = useTextSelection();

  const show = visible && selection.rect && !isSelecting;

  const { refs, floatingStyles, update, middlewareData } = useFloating({
    open: show ?? undefined,
    placement: 'top',
    strategy: 'absolute',
    middleware: [
      inline(),
      offset(8),
      stickyHideWithScroll({
        boundary: container,
        padding: { top: 10, right: 10, bottom: 60, left: 10 },
        stickyThreshold: 75,
      }),
    ],
  });

  // Track if middleware says we're in sticky mode
  const { isSticky, stickyEdge, stickyViewportPosition } =
    middlewareData.stickyHideWithScroll ?? {};

  // Set virtual reference from selection range
  useEffect(() => {
    if (!selection.elem || !selection.range) return;

    refs.setReference({
      getBoundingClientRect: () => selection.range!.getBoundingClientRect(),
      getClientRects: () => selection.range!.getClientRects(),
      contextElement: selection.elem ?? undefined,
    });
  }, [selection.range, selection.elem, refs]);

  // Auto-update position on scroll/resize (but not when sticky)
  useEffect(() => {
    if (!selection.elem || !refs.floating.current || !show) return;

    const cleanup = autoUpdate(selection.elem, refs.floating.current, update);
    return cleanup;
  }, [selection.elem, refs.floating, show, update]);

  const isHidden = middlewareData.stickyHideWithScroll?.referenceHidden;

  const buttonContent = (
    <AnimatePresence>
      {show && (
        <motion.div
          className="flex gap-2 items-center"
          initial={
            isSticky
              ? { y: stickyEdge?.top ? -2 : 4, opacity: 1 }
              : { opacity: 0 }
          }
          animate={{
            y: 0,
            opacity: isHidden ? 0 : 1,
            scale: isHidden ? 0.95 : 1,
            transition: {
              type: 'keyframes',
              duration: 0.1,
            },
          }}
          exit={{
            opacity: 0,
            y: 5,
            transition: {
              delay: 0.15,
              type: 'keyframes',
              duration: 0.1,
            },
          }}
        >
          <Tap>
            <Button
              ref={buttonRef}
              size="sm"
              variant="outline"
              iconSize="md"
              className="pointer-events-auto bg-surface"
              onClick={() => {
                if (selection.text) {
                  draftsStore.appendSelectedText(threadId, selection.text);
                  clearSelection();
                }
              }}
            >
              <XStack className="items-center gap-2 select-none">
                <AppLogo />
                Ask about this
              </XStack>
            </Button>
          </Tap>
          <Tap>
            <IconButton
              icon={Icons.copy}
              size="default"
              scale={0.8}
              variant="outline"
              tooltip="Copy"
              onClick={async () => {
                try {
                  const contentToCopy = selection.markdown || selection.text;
                  await handleCopyText(contentToCopy);
                  clearSelection();
                  toast.info('Copied to clipboard');
                } catch (error) {
                  logError(error, 'Failed to copy selection');
                  toast.error('Oops! Unable to copy to clipboard');
                }
              }}
              className="pointer-events-auto bg-surface border-border-strong hover:border-border-strongest"
            >
              Copy
            </IconButton>
          </Tap>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Always render in container (hidden when sticky, continues tracking)
  const containerVersion = createPortal(
    <div
      className="z-10"
      ref={refs.setFloating}
      style={{
        ...floatingStyles,
        opacity: isSticky ? 0 : 1,
        pointerEvents: isSticky ? 'none' : 'auto',
      }}
    >
      {buttonContent}
    </div>,
    container
  );

  // Conditionally render clone in body (only when sticky)
  const bodyClone =
    isSticky && stickyViewportPosition
      ? createPortal(
          <div
            className="z-10"
            style={{
              position: 'fixed',
              left: stickyViewportPosition.x,
              top: stickyViewportPosition.y,
              transform: 'none',
            }}
          >
            {buttonContent}
          </div>,
          document.body
        )
      : null;

  return (
    <>
      {containerVersion}
      {bodyClone}
    </>
  );
};
